export type PolicyType =
  | 'ROW_LEVEL'
  | 'COLUMN_LEVEL'
  | 'ACCESS_CONTROL'
  | 'TIME_BASED'
  | 'SENSITIVE_DATA';

export type ScopeLevel = 'VAULT' | 'MODEL' | 'ASSET';
export type PolicyStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

export type Classification =
  | 'Public'
  | 'Internal'
  | 'Confidential'
  | 'Restricted'
  | 'PII'
  | 'PHI'
  | 'PCI';

export const CLASSIFICATIONS: Classification[] = ['Public', 'Internal', 'Confidential', 'Restricted', 'PII', 'PHI', 'PCI'];

export type AccessOperation = 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'EXECUTE';

export type ConditionOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'in'
  | 'not_in'
  | 'is_null'
  | 'is_not_null'
  | 'contains'
  | 'contains_any';

export type ValueKind = 'constant' | 'user_var' | 'request_var' | 'resource_var' | 'column';

export interface PolicyScope {
  level: ScopeLevel;
  vaultId: string;
  modelId?: string;
  assetId?: string;
}

export interface ConditionLeaf {
  kind: 'predicate';
  id: string;
  left: { kind: 'column' | 'variable'; name: string };
  operator: ConditionOperator;
  right?: { kind: ValueKind; value: string };
}

export interface ConditionGroup {
  kind: 'group';
  id: string;
  combinator: 'AND' | 'OR';
  children: ConditionNode[];
}

export type ConditionNode = ConditionLeaf | ConditionGroup;

export interface DynamicMatch {
  combinator: 'AND' | 'OR';
  classifications: Classification[];
  tags: string[];
  columnName: string;
  columnPattern: string;
  dataTypes: string[];
}

export interface PolicyTarget {
  mode: 'all' | 'selected' | 'dynamic' | 'current';
  assetIds: string[];
  columnRefs: Array<{ assetId: string; column: string }>;
  match: DynamicMatch;
}

export type MaskKind =
  | 'full_null'
  | 'full_fixed'
  | 'full_string'
  | 'partial_first'
  | 'partial_last'
  | 'partial_email'
  | 'partial_phone'
  | 'partial_custom'
  | 'deny_column'
  | 'custom_hash'
  | 'custom_expression'
  | 'deny_asset'
  | 'none';

export interface MaskAction {
  kind: MaskKind;
  value?: string;
  n?: number;
  expression?: string;
}

export interface SubjectSelector {
  mode: 'everyone' | 'users' | 'roles' | 'groups' | 'attributes' | 'expression';
  userIds: string[];
  roles: string[];
  groups: string[];
  attribute: { key: string; operator: ConditionOperator; value: string };
  expression: string;
}

export interface TimeWindow {
  start: string;
  end: string;
  daysOfWeek: number[];
  dailyStart: string;
  dailyEnd: string;
  timeZone: string;
}

export type PolicyAction =
  | { type: 'ROW_FILTER' }
  | { type: 'COLUMN'; mask: MaskAction }
  | { type: 'ACCESS'; effect: 'allow' | 'deny'; operations: AccessOperation[] }
  | { type: 'TIME'; effect: 'allow_window' | 'deny_window' | 'expire'; window: TimeWindow }
  | { type: 'SENSITIVE'; mask: MaskAction };

export type PolicyDefinitionMode = 'simple' | 'complex';

export interface PolicyRule {
  id: string;
  subject: SubjectSelector;
  action: PolicyAction;
  except?: SubjectSelector;
  condition?: ConditionNode;
  expression?: string;
  definitionMode?: PolicyDefinitionMode;
}

export interface GovernancePolicy {
  id: string;
  name: string;
  description?: string;
  type: PolicyType;
  status: PolicyStatus;
  scope: PolicyScope;
  target: PolicyTarget;
  rules: PolicyRule[];
  advancedExpression: string;
  policyKey: string;
  generateId: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  document?: PolicyDocument;
}

export const POLICY_DOCUMENT_SCHEMA = 'selfic.governance.policy.v1' as const;
export type PolicyDocumentSchema = typeof POLICY_DOCUMENT_SCHEMA;

export interface PolicyDocumentOwner {
  level: ScopeLevel;
  workspaceId: string;
  modelId?: string;
  tableId?: string;
}

export interface PolicyDocumentAssetRef {
  id: string;
  name: string;
}

export interface PolicyDocumentColumnRef {
  assetId: string;
  asset: string;
  column: string;
}

export interface PolicyDocumentAppliesTo {
  mode: PolicyTarget['mode'];
  assets?: PolicyDocumentAssetRef[];
  columns?: PolicyDocumentColumnRef[];
  combinator?: 'AND' | 'OR';
  classifications?: Classification[];
  tags?: string[];
  columnName?: string;
  columnPattern?: string;
  dataTypes?: string[];
}

export type PolicyDocumentWho =
  | { mode: 'everyone' }
  | { mode: 'users'; userIds: string[] }
  | { mode: 'roles'; roles: string[] }
  | { mode: 'groups'; groups: string[] }
  | { mode: 'attributes'; key: string; operator: ConditionOperator; value: string }
  | { mode: 'expression'; expression: string };

export interface PolicyDocumentMask {
  kind: MaskKind;
  value?: string;
  n?: number;
  expression?: string;
}

export interface PolicyDocumentMaskRule {
  who: PolicyDocumentWho;
  mask: PolicyDocumentMask;
  when?: string;
  condition?: ConditionNode;
  except?: PolicyDocumentWho;
  mode?: PolicyDefinitionMode;
}

export interface PolicyDocumentAccessRule {
  who: PolicyDocumentWho;
  effect: 'allow' | 'deny';
  operations: AccessOperation[];
  when?: string;
}

export interface PolicyDocumentTimeRule {
  who: PolicyDocumentWho;
  effect: 'allow_window' | 'deny_window' | 'expire';
  window: TimeWindow;
  when?: string;
}

export interface PolicyDocumentRowRule {
  who: PolicyDocumentWho;
  condition?: ConditionNode;
  when?: string;
  mode?: PolicyDefinitionMode;
}

export interface PolicyDocumentRowDefinition {
  kind: 'row_filter';
  mode: PolicyDefinitionMode;
  expression?: string;
  condition?: ConditionNode;
  who?: PolicyDocumentWho;
  rules?: PolicyDocumentRowRule[];
}

export interface PolicyDocumentColumnDefinition {
  kind: 'column_mask';
  mode: PolicyDefinitionMode;
  rules: PolicyDocumentMaskRule[];
  expression?: string;
}

export interface PolicyDocumentAccessDefinition {
  kind: 'access';
  mode: PolicyDefinitionMode;
  rules: PolicyDocumentAccessRule[];
  expression?: string;
}

export interface PolicyDocumentSensitiveDefinition {
  kind: 'sensitive';
  mode: PolicyDefinitionMode;
  classifications?: Classification[];
  rules: PolicyDocumentMaskRule[];
  expression?: string;
}

export interface PolicyDocumentTimeDefinition {
  kind: 'time';
  mode: PolicyDefinitionMode;
  rules: PolicyDocumentTimeRule[];
  expression?: string;
}

export type PolicyDocumentDefinition =
  | PolicyDocumentRowDefinition
  | PolicyDocumentColumnDefinition
  | PolicyDocumentAccessDefinition
  | PolicyDocumentSensitiveDefinition
  | PolicyDocumentTimeDefinition;

export interface PolicyDocumentMeta {
  version: number;
  generatedId: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyDocument {
  schema: PolicyDocumentSchema;
  path: string;
  id: string;
  key: string;
  name: string;
  description?: string;
  type: PolicyType;
  status: PolicyStatus;
  owner: PolicyDocumentOwner;
  appliesTo: PolicyDocumentAppliesTo;
  definition: PolicyDocumentDefinition;
  meta: PolicyDocumentMeta;
}

export interface PolicyColumn {
  name: string;
  type: string;
  classification?: Classification;
  tags: string[];
  nullable: boolean;
}

export interface PolicyAsset {
  id: string;
  name: string;
  vaultId: string;
  modelIds: string[];
  connectionType: string;
  sourceId?: string;
  path?: string;
  classification?: Classification;
  tags: string[];
  columns: PolicyColumn[];
  rows: Array<Record<string, unknown>>;
}

export interface PolicyModel {
  id: string;
  name: string;
  vaultId: string;
}

export interface PolicyVault {
  id: string;
  name: string;
  connections: string[];
  modelIds: string[];
}

export interface SyntheticUser {
  id: string;
  name: string;
  email: string;
  role: string;
  roles: string[];
  groups: string[];
  department: string;
  region: string;
  tenantId: string;
  employeeId: string;
  managerId?: string;
  clearance: string;
  channel: string;
}

export interface VariableMeta {
  path: string;
  type: 'STRING' | 'STRING[]' | 'NUMBER' | 'TIMESTAMP' | 'BOOLEAN';
  description: string;
  example: string;
}

export interface FunctionMeta {
  name: string;
  signature: string;
  description: string;
}

export interface ExpressionIssue {
  message: string;
  from: number;
  to: number;
  severity: 'error' | 'warning';
  unsupported?: boolean;
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
  issues: ExpressionIssue[];
  unsupported: boolean;
}

export interface ImpactPreview {
  assetsInScope: number;
  assetsMatched: Array<{ id: string; name: string; modelNames: string[] }>;
  columnsProtected: Array<{ asset: string; column: string; classification?: string }>;
  modelsAffected: string[];
  usersAffected: string[];
  inherited: string[];
  conflicts: string[];
  missingClassifications: string[];
  typeIncompatibilities: string[];
  unsupported: string[];
}

export interface SimulationRequest {
  userId: string;
  assetId?: string;
  column?: string;
  operation: AccessOperation;
  timestamp: string;
}

export interface SimulationResult {
  decision: 'allowed' | 'denied' | 'masked' | 'filtered' | 'limited';
  explanation: string;
  origins: Array<{ policyId: string; policyName: string; level: ScopeLevel; reason: string }>;
  matchedConditions: string[];
  allowedRows?: Array<Record<string, unknown>>;
  excludedRows?: Array<Record<string, unknown>>;
  rowCounts?: { total: number; allowed: number; filtered: number };
  beforeAfter?: Array<{ column: string; before: unknown; after: unknown }>;
  unsupported?: string;
}

export const POLICY_TYPE_KIND = {
  ROW_LEVEL: 'Row Filter',
  COLUMN_LEVEL: 'Column Mask',
  ACCESS_CONTROL: 'Data Access',
  TIME_BASED: 'Time Access',
  SENSITIVE_DATA: 'Classification',
} as const;

export const KIND_POLICY_TYPE = {
  'Row Filter': 'ROW_LEVEL',
  'Column Mask': 'COLUMN_LEVEL',
  'Data Access': 'ACCESS_CONTROL',
  'Time Access': 'TIME_BASED',
  Classification: 'SENSITIVE_DATA',
} as const;

export const POLICY_TYPE_LABEL: Record<PolicyType, string> = {
  ROW_LEVEL: 'Row-Level Policy',
  COLUMN_LEVEL: 'Column-Level Policy',
  ACCESS_CONTROL: 'Access Control Policy',
  TIME_BASED: 'Time-Based Access Policy',
  SENSITIVE_DATA: 'Sensitive Data Policy',
};

export const EXAMPLE_EXPRESSIONS: Record<PolicyType, string> = {
  ROW_LEVEL: 'tenant_id = ${user.tenant_id}\nAND region = ${user.region}',
  COLUMN_LEVEL: "NOT ('admin' IN ${user.roles})",
  ACCESS_CONTROL: "'Finance' IN ${user.groups}\nAND ${request.operation} = 'SELECT'",
  TIME_BASED: "${request.timestamp} >= '2026-11-01T00:00:00Z'\nAND ${request.timestamp} < '2026-12-01T00:00:00Z'",
  SENSITIVE_DATA: "${resource.classification} IN ('PII', 'Restricted')\nAND 'External' IN ${user.groups}",
};

export const OPERATORS: Array<{ id: ConditionOperator; label: string; sql: string }> = [
  { id: 'eq', label: 'Equals', sql: '=' },
  { id: 'neq', label: 'Not equals', sql: '!=' },
  { id: 'gt', label: 'Greater than', sql: '>' },
  { id: 'lt', label: 'Less than', sql: '<' },
  { id: 'gte', label: 'Greater than or equal', sql: '>=' },
  { id: 'lte', label: 'Less than or equal', sql: '<=' },
  { id: 'in', label: 'IN', sql: 'IN' },
  { id: 'not_in', label: 'NOT IN', sql: 'NOT IN' },
  { id: 'is_null', label: 'IS NULL', sql: 'IS NULL' },
  { id: 'is_not_null', label: 'IS NOT NULL', sql: 'IS NOT NULL' },
  { id: 'contains', label: 'CONTAINS', sql: 'CONTAINS' },
  { id: 'contains_any', label: 'CONTAINS ANY', sql: 'CONTAINS ANY' },
];
